import type {
  CreateReviewRequest,
  CreateReviewResponse,
  GetUserReviewResponse,
  DeleteUserReviewResponse,
  ReviewResponse,
  SecureUserMaterialReviewResponse,
} from '@mrb91002/shared-types'

import { secureFetch, SECURE_API_BASE } from './helpers'

export async function createOrUpdateReview(
  patternId: string,
  reviewData: CreateReviewRequest,
  idToken: string,
): Promise<CreateReviewResponse> {
  return secureFetch<CreateReviewResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/review`, idToken, {
    method: 'POST',
    body: JSON.stringify(reviewData),
  })
}

export async function createOrUpdateMaterialReview(
  materialId: string,
  reviewData: CreateReviewRequest,
  idToken: string,
): Promise<CreateReviewResponse> {
  return secureFetch<CreateReviewResponse>(`${SECURE_API_BASE}/material/${encodeURIComponent(materialId)}/review`, idToken, {
    method: 'POST',
    body: JSON.stringify(reviewData),
  })
}

export async function getUserReview(patternId: string, idToken: string): Promise<GetUserReviewResponse> {
  return secureFetch<GetUserReviewResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/my-review`, idToken, {
    method: 'GET',
  })
}

/**
 * The single response item returned by POST /pattern/{patternId}/review/{reviewerId}/response.
 *
 * Declared locally (rather than importing `CreateReviewResponseSuccessResponse`/`ReviewResponseItem`
 * from `@mrb91002/shared-types`) so this consumer does not depend on a not-yet-published shared-types
 * export — the canonical shape still lives in shared-types (`api/pattern.ts`). Mirror it exactly.
 */
export interface RespondToReviewResult {
  message: string
  response: {
    response_id: string
    entity_type: string
    entity_id: string
    parent_review_id: string
    responder_id: string
    responder_type: string
    text: string
    created_at: string
    depth: number
    is_hidden: boolean
  }
}

/**
 * Post an owner/reviewer response to a pattern review.
 *
 * Backend: lambda-secure `createReviewResponse` (POST /api/secure/pattern/{patternId}/review/
 * {reviewerId}/response). The pattern owner OR the original reviewer may respond (others get 403);
 * an owner response also locks the review text and sets `has_owner_response`. The counterparty is
 * notified with a `review_response` notification. `reviewerId` is the review's `review_id` (the
 * reviewer's identity id, which the public reviews listing carries as `review_id`).
 */
export async function respondToReview(
  patternId: string,
  reviewerId: string,
  text: string,
  idToken: string,
): Promise<RespondToReviewResult> {
  return secureFetch<RespondToReviewResult>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/review/${encodeURIComponent(reviewerId)}/response`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ text }),
    },
  )
}

/**
 * Fetch the authenticated caller's OWN review for a material from the SECURE lambda.
 *
 * The public material page (lambda-main `/api/materials/{id}`) has NO Cognito authorizer, so it
 * cannot resolve the caller's identity there — mirror how pattern review data is personalized by
 * asking the SECURE endpoint (authorizer-backed) for the caller's own review. The secure route
 * (`GET /api/secure/material/{id}/review/user`, handler lambda-secure getUserMaterialReview) responds
 * 404 when the caller has no review, which we normalize to `null`. The result is shaped as
 * `ReviewResponse` so `CommentsReviews` can render "View Your Review" for a returning reviewer.
 */
export async function getUserMaterialReview(materialId: string, idToken: string): Promise<ReviewResponse | null> {
  try {
    // Cache-bust: this is a per-user, MUTABLE resource ("my current review"). CloudFront
    // negative-caches the 404 for this GET (keyed by URL + Authorization, ~10s error-cache TTL),
    // so a load BEFORE the user has reviewed poisons the cache — the load right AFTER they submit
    // then keeps getting the stale 404 and "View Your Review" never appears (BF-062i). The
    // ApiCachePolicy keys on ALL query strings, so a unique param forces a fresh origin fetch each
    // navigation/refresh. loadMaterial is called once per nav/refresh (not polled), so this is not a
    // request storm.
    const res = await secureFetch<SecureUserMaterialReviewResponse>(
      `${SECURE_API_BASE}/material/${encodeURIComponent(materialId)}/review/user?_cb=${Date.now()}`,
      idToken,
      { method: 'GET' },
    )
    const r = res.review
    if (!r) return null
    return {
      review_id: r.review_id,
      // pattern_id/version are not meaningful for material reviews but are required by ReviewResponse;
      // carry the materialId and 0 so the shared shape consumed by CommentsReviews is satisfied.
      pattern_id: materialId,
      version: 0,
      rating: r.rating,
      text: r.text,
      images: r.images ?? [],
      created_at: r.created_at,
      updated_at: r.updated_at ?? r.created_at,
      reviewer: { screen_name: '', photo: '' },
    }
  } catch (err: unknown) {
    // The secure route returns 404 when the caller has no review for this material — treat as "none".
    if (typeof err === 'object' && err !== null && (err as { status?: number }).status === 404) {
      return null
    }
    throw err
  }
}

export async function deleteUserReview(patternId: string, idToken: string): Promise<DeleteUserReviewResponse> {
  return secureFetch<DeleteUserReviewResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/my-review`, idToken, {
    method: 'DELETE',
  })
}
